import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import {
  announceSignOut,
  clearSessionHint,
  requestIdentityProviderLogout,
  setSessionHint,
} from './auth';
import navigation from './navigation.json';

type NavigationItem = { text: string; path?: string; app?: string; icon?: string; sections?: string[] };
type AccountItem = { text: string; path?: string; action?: string; codePath?: string };

// One definition for every application behind the top bar, see infra/forgejo/skin.
const GLOBAL_ITEMS: NavigationItem[] = navigation.global;
const UTILITY_ITEMS: NavigationItem[] = navigation.utility;
// Entries with only a `code` target exist on code.overheid.nl alone. A `codePath` lives there too,
// but the portal links to it.
const ACCOUNT_ITEMS: AccountItem[] = navigation.account.items.filter(
  (item: AccountItem) => item.path || item.action || item.codePath,
);

function useProfile() {
  const identityApi = useApi(identityApiRef);
  const [profile, setProfile] = useState<{ displayName?: string; email?: string }>({});
  useEffect(() => {
    identityApi
      .getProfileInfo()
      .then(setProfile)
      .catch(() => undefined);
  }, [identityApi]);
  return profile;
}

function TopNavigation() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const profile = useProfile();
  const displayName = profile.displayName ?? 'Mijn account';
  const accountMenu = useRef<HTMLElement>(null);

  const codeUrl = config.getString('platform.codeUrl').replace(/\/$/, '');

  useEffect(() => setSessionHint(codeUrl), [codeUrl]);

  useEffect(() => {
    const menu = accountMenu.current;
    const onSelect = (event: Event) => {
      const item = event.target as HTMLElement;
      if (item.dataset.action === 'sign-out') {
        clearSessionHint(codeUrl);
        announceSignOut(codeUrl);
        requestIdentityProviderLogout();
        identityApi.signOut();
        return;
      }
      // nldd-menu-bar-item cancels every click inside its menu, so the link in a menu item
      // never navigates by itself, and `select` is all that remains.
      const href = item.getAttribute('href');
      if (href?.startsWith('/')) {
        navigate(href);
      } else if (href) {
        window.location.assign(href);
      }
    };
    menu?.addEventListener('select', onSelect);
    return () => menu?.removeEventListener('select', onSelect);
  }, [codeUrl, identityApi, navigate]);

  const accountHref = (item: AccountItem) => (item.codePath ? `${codeUrl}${item.codePath}` : item.path);

  return (
    <nldd-skip-link slot="header" href="#inhoud" text="Ga direct naar de inhoud">
      <nldd-top-navigation-bar
        logo-title="Nederlandse Digitale Dienst"
        logo-subtitle="Ministerie van Economische Zaken en Klimaat"
        website-title={navigation.title}
        website-href="/"
      >
        <nldd-menu-bar slot="global">
          {GLOBAL_ITEMS.map(item => {
            const href = item.path ?? codeUrl;
            return (
              <nldd-menu-bar-item
                key={item.text}
                text={item.text}
                href={href}
                current={
                  [item.path, ...(item.sections ?? [])].some(path => path && pathname.startsWith(path))
                    ? ''
                    : undefined
                }
              />
            );
          })}
        </nldd-menu-bar>
        <nldd-menu-bar slot="utility">
          {UTILITY_ITEMS.map(item => (
            <nldd-menu-bar-item
              key={item.text}
              text={item.text}
              icon={item.icon}
              content-priority="text"
              href={item.path}
            />
          ))}
          <nldd-menu-bar-item
            text={displayName}
            icon="account"
            icon-only=""
            expandable=""
          >
            <nldd-menu ref={accountMenu} width="340px">
              <nldd-container slot="header" padding="12">
                <nldd-title size="6" heading-level="2" text={displayName} supporting-text={profile.email} />
              </nldd-container>
              {ACCOUNT_ITEMS.map(item => (
                <nldd-menu-item
                  key={item.text}
                  text={item.text}
                  href={accountHref(item)}
                  data-action={item.action}
                />
              ))}
            </nldd-menu>
          </nldd-menu-bar-item>
        </nldd-menu-bar>
      </nldd-top-navigation-bar>
    </nldd-skip-link>
  );
}

export const topNavigation = NavContentBlueprint.make({
  params: { component: () => <TopNavigation /> },
});
